<script setup lang="ts">
import { mdiArrowLeftBold } from '@mdi/js'

const runtimeConfig = useRuntimeConfig()

const route = useRoute()
const { data: docs } = await useAsyncData(route.path, () => queryCollection('content').path(route.path.toLowerCase()).first())

useSeoMeta({
  title: `${docs.value?.title} | Code & Magic`,
  description: docs.value?.description || '夢中になれたら、学びも遊び。楽しむことから始める、紙とウェブのプログラミング教材。',
  ogTitle: `${docs.value?.title} | Code & Magic`,
  ogDescription:
    docs.value?.description || '夢中になれたら、学びも遊び。楽しむことから始める、紙とウェブのプログラミング教材。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700;800&display=swap' },
  ],
})

definePageMeta({
  layout: 'simple-landing',
})
</script>

<template>
  <div class="docs-bg">
    <div class="centered-area">
      <div class="rounded-area">
        <template v-if="docs">
          <div class="docs-card">
            <div class="docs-body">
              <ContentRenderer
                :value="docs"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="not-found">
            <h1>Page Not Found</h1>
            <p>Oops! The content you're looking for doesn't exist.</p>
          </div>
        </template>

        <nav class="docs-nav">
          <NuxtLink
            to="/"
            class="nav-link"
          >
            <v-icon
              :icon="mdiArrowLeftBold"
              size="small"
            />
            ホームに戻る
          </NuxtLink>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '~/assets/css/landing-variables.css';

.docs-bg {
  font-family: "Noto Serif JP", "Noto Sans JP", serif;
  color: var(--landing-color-text);
  width: 100%;
  max-width: 100%;
  padding: 0;
}

.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.rounded-area {
  margin: 0 0.5em;
  padding: var(--landing-rounded-padding);
  background-color: var(--landing-color-card-warm);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
}

.docs-card {
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  overflow: hidden;
}

.docs-body {
  padding: 1.5rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.7;
}

.docs-body :deep(h1) {
  color: var(--landing-color-navy);
  font-size: 1.8rem;
  font-weight: 900;
  margin-bottom: 1rem;
}

.docs-body :deep(h2) {
  font-size: 1.25rem;
  font-weight: 900;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.3rem;
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.docs-body :deep(h2 a) {
  color: var(--landing-color-text);
  text-decoration: none;
}

.docs-body :deep(h3) {
  font-size: 1.1rem;
  font-weight: 900;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.docs-body :deep(h3 a) {
  color: var(--landing-color-text);
  text-decoration: none;
}

.docs-body :deep(h4) {
  font-size: 1rem;
  font-weight: 900;
  margin-top: 1.2rem;
  margin-bottom: 0.4rem;
}

.docs-body :deep(h4 a) {
  color: var(--landing-color-text);
  text-decoration: none;
}

.docs-body :deep(p) {
  margin-bottom: 0.85rem;
}

.docs-body :deep(ol),
.docs-body :deep(ul) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.docs-body :deep(ol ul) {
  padding-left: 1.4rem;
}

.docs-body :deep(li) {
  margin-bottom: 0.3rem;
}

.docs-body :deep(strong) {
  color: var(--landing-color-navy);
  font-weight: 900;
}

.docs-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.docs-body :deep(table th) {
  text-align: left;
  font-weight: 900;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.docs-body :deep(table td) {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--landing-color-border-mint);
}

.docs-body :deep(blockquote) {
  background-color: var(--landing-color-card-mint);
  border-left: 4px solid var(--landing-color-teal);
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  border-radius: 0 var(--landing-radius) var(--landing-radius) 0;
}

.docs-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  margin: 0.5rem 0;
}

.not-found {
  text-align: center;
  padding: 4rem 1rem;
}

.docs-nav {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--landing-color-border-mint);
}

.nav-link {
  color: var(--landing-color-teal-dark);
  text-decoration: none;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.nav-link:hover {
  text-decoration: underline;
}
</style>
